/* eslint-disable ts/strict-boolean-expressions */
// The above eslint-disable is temporary while we migrate from JS to TS
// The queries.js file and several components are still untyped

import { Tabs, TabsContent, TabsList, TabsTrigger, Text } from "@sentricmusic/ui";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetAlbumSuggestions, useGetArtistMeta, useGetSuggestions } from "@/api/queries";
import SocialSpotify from "@/components/SocialSpotify";
import useAppStore from "@/store/appStateZustand";

import AlbumSuggestionsTable from "./AlbumSuggestionsTable";
import { SongSuggestionsSkeleton } from "./components/SongSuggestionsSkeleton";
import SuggestionsTable from "./SuggestionsTable";

type SongSuggestionsProps = {
  highlightColour?: string;
};

function SongSuggestions({ highlightColour }: SongSuggestionsProps) {
  const { t } = useTranslation("songs");
  const flags = useFlags();
  const [showSpotifyForm, setShowSpotifyForm] = useState(true);
  const [activeTab, setActiveTab] = useState("albums");
  const selectedAccount = useAppStore(s => s.selectedAccount) as string;
  const artistMeta = useGetArtistMeta({ aac: selectedAccount });

  useEffect(() => {
    if (selectedAccount) {
      void artistMeta.refetch();
    }
  }, [selectedAccount, artistMeta]);

  const spotifyID: string | null = (() => {
    try {
      if (!artistMeta?.data?.spotify_obj)
        return null;
      const parsed: { id?: string } = JSON.parse(artistMeta.data.spotify_obj) as { id?: string };
      return parsed?.id ?? null;
    }
    catch (error) {
      console.warn("[SongSuggestions] Invalid JSON in spotify_obj:", artistMeta?.data?.spotify_obj, error);
      return null;
    }
  })();

  const suggestions = useGetSuggestions({
    spotifyid: spotifyID,
    accountId: selectedAccount,
  });

  const albumSuggestions = useGetAlbumSuggestions(spotifyID);

  if (!flags["pd-spotify-suggestions-feature"])
    return null;

  return (
    <div>
      {artistMeta.isLoading && <SongSuggestionsSkeleton />}

      {artistMeta.isSuccess
        && !artistMeta?.data?.spotify_obj
        && showSpotifyForm
        && flags["pd-spotify-link-account-feature"] && (
        <div className="mb-4">
          <SocialSpotify closeForm={() => setShowSpotifyForm(false)} />
        </div>
      )}

      {spotifyID != null && (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="albums">{t("songs:album-suggestions-tab", "Album Suggestions")}</TabsTrigger>
            <TabsTrigger value="songs">{t("songs:song-suggestions-tab", "Song Suggestions")}</TabsTrigger>
          </TabsList>

          <TabsContent className="py-4" value="songs">
            <SuggestionsTable
              data={suggestions.data ?? []}
              highlightColour={highlightColour}
              isLoading={suggestions.isLoading}
            />
          </TabsContent>

          <TabsContent className="py-4" value="albums">
            {albumSuggestions.error && (
              <div className="p-4">
                <Text as="p" className="text-ink-error">
                  {t("songs:error-loading-album-suggestions", "Error loading album suggestions:")}
                  {" "}
                  {albumSuggestions.error.message}
                </Text>
              </div>
            )}
            <AlbumSuggestionsTable
              data={albumSuggestions.data ?? []}
              highlightColour={highlightColour}
              spotifyArtistId={spotifyID ?? undefined}
              isLoading={albumSuggestions.isLoading}
            />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

export default SongSuggestions;
